/**
 * Confirmation + success dialog for admin-issued password resets and
 * invitation re-sends. Both flows use the same dialog because they're
 * structurally identical:
 *
 *   1. Confirm step: "Are you sure?" + the specific consequences
 *   2. Issue the token (POST to backend)
 *   3. Success step: show the URL with a copy-link affordance
 *
 * The backend emails the user the new link via SendGrid and also
 * returns the URL so the admin has a fallback to hand-deliver via
 * Slack / DM if the email never arrives (delivery is best-effort
 * so a failed send doesn't block the action).
 */

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  resendUserInvitation,
  resetUserPassword,
  type AdminIssueResetResponse,
  type UserListItem,
} from '../../api/admin';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

type Mode = 'reset' | 'resend';

interface ResetPasswordDialogProps {
  open: boolean;
  user: UserListItem;
  mode: Mode;
  onClose: () => void;
}

const COPY: Record<
  Mode,
  {
    icon: React.ReactNode;
    title: string;
    body: string;
    confirmLabel: string;
    successAlert: string;
    successTitle: string;
    successHelper: string;
  }
> = {
  reset: {
    icon: <KeyIcon sx={{ fontSize: 18 }} />,
    title: 'Reset Password?',
    body:
      'This emails the user a one-time link to choose a new password. They will be forced to ' +
      'set a fresh password on their next sign-in. Any open browser session keeps working until ' +
      'they hit a protected page.',
    confirmLabel: 'Send Reset Link',
    successAlert: 'Password reset email sent to',
    successTitle: 'Reset Link Sent',
    successHelper:
      'The email contains a one-time link to choose a new password. It expires in 24 hours ' +
      'and can only be used once.',
  },
  resend: {
    icon: <MailOutlineIcon sx={{ fontSize: 18 }} />,
    title: 'Resend Invitation?',
    body:
      'The original set-password link is no longer valid. This emails the user a fresh one ' +
      'they can use to choose their initial password.',
    confirmLabel: 'Send New Invitation',
    successAlert: 'Invitation email re-sent to',
    successTitle: 'Invitation Re-sent',
    successHelper:
      'The email contains a fresh one-time link to set their password. It expires in 24 hours ' +
      'and can only be used once.',
  },
};

export function ResetPasswordDialog({ open, user, mode, onClose }: ResetPasswordDialogProps) {
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState<AdminIssueResetResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const copy = COPY[mode];

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'reset' ? resetUserPassword(user.id) : resendUserInvitation(user.id),
    onSuccess: (data) => {
      setSuccess(data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const handleClose = () => {
    setSuccess(null);
    setCopied(false);
    mutation.reset();
    onClose();
  };

  const copyLink = async () => {
    if (!success?.reset_url) return;
    await navigator.clipboard.writeText(success.reset_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { m: { xs: 2, sm: 4 } } } }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(187, 140, 63, 0.12)',
            color: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {copy.icon}
        </Box>
        {success ? copy.successTitle : copy.title}
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ fontFamily: FONT_INTER }}>
              {copy.successAlert} <strong>{user.email}</strong>.
            </Alert>
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: 13.5, color: 'rgba(15,10,7,0.7)' }}>
              {copy.successHelper}
            </Typography>
            {success.reset_url && (
              <Box
                sx={{
                  borderTop: '1px solid rgba(187, 140, 63, 0.22)',
                  pt: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'rgba(15, 10, 7, 0.55)',
                    mb: 0.75,
                  }}
                >
                  Fallback link
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    color: 'rgba(15, 10, 7, 0.6)',
                    mb: 1.25,
                  }}
                >
                  If the email doesn't arrive, copy this link and share it directly.
                </Typography>
                <TextField
                  label={mode === 'reset' ? 'Reset link' : 'Invitation link'}
                  value={success.reset_url}
                  fullWidth
                  size="small"
                  multiline
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: 11.5 },
                  }}
                />
                <Button
                  startIcon={<ContentCopyIcon />}
                  size="small"
                  onClick={copyLink}
                  sx={{
                    mt: 1.25,
                    fontFamily: FONT_INTER,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.25,
                bgcolor: 'rgba(187, 140, 63, 0.06)',
                border: '1px solid rgba(187, 140, 63, 0.18)',
              }}
            >
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 600, color: INK }}
              >
                {user.first_name} {user.last_name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: 'rgba(15,10,7,0.6)',
                }}
              >
                {user.email}
              </Typography>
            </Box>
            <Typography
              sx={{ fontFamily: FONT_INTER, fontSize: 13.5, color: 'rgba(15,10,7,0.72)' }}
            >
              {copy.body}
            </Typography>
            {mutation.error && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {success ? (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              fontFamily: FONT_INTER,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 12,
              px: 2.25,
              boxShadow: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
              },
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
              sx={{
                background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                color: INK,
                fontFamily: FONT_INTER,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: 12,
                px: 2.25,
                boxShadow: 'none',
                '&:hover': {
                  background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                  boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
                },
              }}
            >
              {mutation.isPending ? 'Issuing…' : copy.confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
